import { ReactNode, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ExpandableSectionProps {
  title: string;
  icon: ReactNode;
  count: number;
  children: ReactNode;
  initialLimit?: number;
  isExpanded?: boolean;
  onToggle?: () => void;
  headerActions?: ReactNode;
  alwaysExpanded?: boolean;
  defaultExpanded?: boolean;
}

export function ExpandableSection({
  title,
  icon,
  count,
  children,
  initialLimit,
  isExpanded: controlledExpanded,
  onToggle,
  headerActions,
  alwaysExpanded = false,
  defaultExpanded = false,
}: ExpandableSectionProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  
  const isExpanded = alwaysExpanded || (onToggle ? controlledExpanded : internalExpanded);
  const handleToggle = () => {
    if (alwaysExpanded) return;
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-muted-foreground">{icon}</div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <Badge variant="secondary" className="ml-2">
              {count}
            </Badge>
          </div>
          {headerActions && (
            <div className="flex items-center gap-2">
              {headerActions}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className={cn("space-y-2", count === 0 && "pb-6")}>
        {count === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No {title.toLowerCase()} yet
          </p>
        ) : (
          <>
            {children}
            {!alwaysExpanded && (
              <div className="pt-4 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggle}
                  className="gap-1"
                >
                  {isExpanded ? (
                    <>
                      Show Less <ChevronUp className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show All ({count}) <ChevronDown className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
